import Vue from 'vue';
import { ChannelNameSpace, CompsBridge, CompsNameSpace } from '../../MessageChannel';
import { Watch } from '../../Worker/Watch';
import { DataSync } from '../../Worker/DataSync';
import { Indicators } from '@iv/shared/src/reportSla';
import { loadScript } from '@iv/shared/src/load';
import { log } from '../utils';

const isMainThread = function () {
  return !!window.interactWorker;
};
const workerCompsMap = new Map();
const workerInfoMap = new Map();
const workerChildCompsIdMap = new Map();

export default {
  data() {
    return {
      // 存放nodeId -》props
      propsData: {},
      // 存放nodeId -》 templateId
      compsList: [],
      // 存放非自动释放的组件
      shouldNotRelease: {},
      // 全局组件
      globalCompsList: [],
      // 已加载的comps
      loadedComps: {},
      // 当前播放是视频比例
      ratio: 1.7,
    };
  },
  methods: {
    // 加载组件url
    async loadComps(compsUrl) {
      let url;
      if (compsUrl.indexOf('127.0.0.1') > -1) {
        url = compsUrl;
      } else {
        url = compsUrl.replace('http:', '');
      }

      const loadScriptStartTime = Number(new Date());
      await loadScript(url);
      const loadScriptEndTime = Number(new Date());

      await this.initComps();

      const loadTime = loadScriptEndTime - loadScriptStartTime;
      this.iBridge.invoke(ChannelNameSpace.engine, 'reportLog', `${url}组件加载耗时：${loadTime}`);
      if (loadTime > 1000) {
        // 上报performance
        if ('performance' in window) {
          const resources = window.performance.getEntriesByType('resource');
          const resource = resources.filter(s => s.initiatorType === 'script').find(s => s.name === url);
          this.iBridge.invoke(ChannelNameSpace.engine, 'reportLog', `${url}组件performance指标：${JSON.stringify(resource)}`);
        }
      }
    },
    // 注册组件
    initComps() {
      const promiseArray = [];
      const interactComps = window['_' + 'interactComps'];
      if (interactComps) {
        Object.keys(interactComps).forEach((templateId) => {
          const promiseItem = new Promise((resolve) => {
            this.iBridge.asyncEmit('beforeCompsInit', templateId).then(() => {
              if (!this.loadedComps[templateId]) {
                log('regist', templateId);

                if (isMainThread()) { // 使用worker作为运行环境需要对组件构造对象做特殊处理
                  // 主线程组件的data是经过了worker同步代理的
                  interactComps[templateId].data = function () {
                    if (!this.root) {
                      // 重新赋值 nodeid
                      this.nodeid = workerChildCompsIdMap.get(this.nodeid).shift();
                      // 挂载组件间通信消息
                      this.interactCompsEmitCb = (data) => {
                        if (data.compsId === this.nodeid) {
                          const ds = workerCompsMap.get(this.nodeid).dataSync;
                          this.$emit(...data.data.map(param => ds.parse(param)));
                        }
                      };
                      window.iBridge.on(CompsNameSpace.worker, 'emit', this.interactCompsEmitCb);
                    }
                    return workerCompsMap.get(this.nodeid).data;
                  };
                  const methods = {};
                  // 主线程组件的方法需要同步到worker
                  (workerInfoMap.get(templateId) || []).forEach((method) => {
                    methods[method] = function (...params) {
                      // 需要屏蔽互动组件平台构建时注入的特定方法
                      if (['proxy'].includes(method)) {
                        return;
                      }
                      const args = params.map((param) => {
                        const ds = workerCompsMap.get(this.nodeid).dataSync;
                        let p = param;
                        if (param instanceof Event) {
                          // Event | KeyboardEvent | MouseEvent | TouchEvent ，组装一些通用的字段信息
                          p = {
                            type: param.type,
                            pageX: 'pageX' in param ? param.pageX : undefined,
                            pageY: 'pageY' in param ? param.pageY : undefined,
                            offsetX: 'offsetX' in param ? param.offsetX : undefined,
                            offsetY: 'offsetY' in param ? param.offsetY : undefined,
                          };
                        }
                        return ds.init(p, true);
                      });
                      window.iBridge.invoke(CompsNameSpace.worker, 'method', this.nodeid, method, args);
                    };
                  });
                  interactComps[templateId].methods = methods;
                }
                // 主线程组件销毁同步到worker
                interactComps[templateId].destroyed = function () {
                  workerCompsMap.delete(this.nodeid);
                  workerChildCompsIdMap.delete(this.nodeid);
                  if (this.interactCompsEmitCb) {
                    window.iBridge.off(CompsNameSpace.worker, 'emit', this.interactCompsEmitCb);
                  }
                  window.iBridge.invoke(CompsNameSpace.worker, 'destroy', this.nodeid);
                };

                Vue.component(templateId, interactComps[templateId]);
                this.loadedComps[templateId] = true;
              }
              resolve();
            });
          });
          promiseArray.push(promiseItem);
        });
      } else {
        log('组件js没有返回有效代码');
      }
      return Promise.all(promiseArray);
    },
    // 销毁消息通道
    destroyBridge() {
      CompsBridge.destroy();
    },
    handleInterrupt(id) {
      this.shouldNotRelease[id] = true;
    },
    handleRequest(id) {
      this.shouldNotRelease[id] = false;
      this.compsList = this.compsList.filter(compInfo => compInfo.id !== id);
    },
    // 初始化worker内的组件实例
    async initWorker({ componentId, id, componentUrl }, props, childrenCfg) {
      // 通知worker开始实例化组件
      this.iBridge.invoke(CompsNameSpace.worker, 'init', {
        templateId: componentId,
        id,
        componentUrl,
      }, props);
      // worker实例化组件ready后的回调
      const initWorkerComps = new Promise((resolve) => {
        const onInit = (data) => {
          if (data.compsId !== id) {
            return;
          }
          workerInfoMap.set(`V${componentId}`, data.methods);
          // 初始化主线程的数据同步对象
          const ds = new DataSync(id);
          Object.assign(data.data[2], data.computed[2]);
          const workerData = ds.parse(data.data);
          // 监听数据同步对象的变更，把变更发送给worker
          const watch = new Watch(workerData);
          watch.doWatch((curr, prev, changes) => {
            const patchs = ds.organizePatchs(watch.original, changes);
            this.iBridge.invoke(CompsNameSpace.worker, 'patch', id, patchs);
          });
          // 监听worker的数据更新，应用到数据同步对象上
          this.iBridge.on(CompsNameSpace.worker, 'update', (data) => {
            Object.assign(data.data[2], data.computed[2]);
            ds.compareAndPatch(data.data);
            watch.refresh();
          });
          resolve({
            data: watch.source,
            dataSync: ds,
          });
          // 执行完成后移除ready监听回调
          this.iBridge.off(CompsNameSpace.worker, 'ready', onInit);
        };
        // 接收worker实例化完成消息
        this.iBridge.on(CompsNameSpace.worker, 'ready', onInit);
      });

      workerCompsMap.set(id, await initWorkerComps);

      // 处理组件使用到的子组件
      if (!childrenCfg || !Array.isArray(childrenCfg) || childrenCfg.length === 0) {
        return;
      }
      await Promise.all(childrenCfg.map(async (cfg) => {
        const { key: childKey, compsId: childCompsId } = cfg;
        const children = props[childKey];
        // 根据配置遍历和初始化子组件
        for (let i = 0, j = children.length; i < j; i++) {
          const childId = `${id}-${childId}-${i}`;
          let idList = [];
          if (workerChildCompsIdMap.get(id)) {
            idList = workerChildCompsIdMap.get(id);
          }
          idList.push(childId);
          workerChildCompsIdMap.set(id, idList);
          await this.initWorker({
            componentId: childCompsId,
            id: childId,
            componentUrl,
          }, children[i], cfg.childrenCfg);
        }
      }));
    },
    // worker组件实例初始化完成释放缓存
    releaseWorkerInitEventAndData() {
      workerInfoMap.clear();
    },
  },
  mounted() {
    const wrapper = document.getElementById('components-wrap');
    if (wrapper) {
      wrapper.addEventListener('touchmove', (e) => {
        e.preventDefault();
      });
    }
    this.iBridge = CompsBridge.init();
    // 编辑模式下 初次加载
    this.$forceUpdate();
    this.iBridge.on(ChannelNameSpace.engine, 'videoNodeChange', (videoInfo) => {
      this.compsList = this.compsList.filter(compInfo => this.shouldNotRelease[compInfo.id]);
      const { streamRatio } = videoInfo;
      this.ratio = streamRatio;
    });

    // 从接口更新互动信息
    this.iBridge.on(ChannelNameSpace.engine, 'interactInfoUpdate', async (interactInfo) => {
      if (interactInfo) {
        this.iBridge.setCurrentNodeId(interactInfo.id);
      }

      // 清空组件列表
      this.compsList = this.compsList.filter(compInfo => this.shouldNotRelease[compInfo.id]);
      log('组件运行环境：互动信息更新', interactInfo);
      const {
        interacts, // 互动节点列表
        components, // 组件js列表
      } = interactInfo;

      const compsMap = {};
      components.forEach((component) => {
        const { id } = component;
        compsMap[id] = component;
      });

      const newCompsList = [];
      await Promise.all(interacts.map(async (interactItem) => {
        const { id, componentId, interactConfig } = interactItem;
        if (interactItem.type === 2) {
          // 全局组件
          this.handleInterrupt(interactItem.id);
          const filterFunc = comp => comp.id === interactItem.id;
          if (this.compsList.some(filterFunc) || newCompsList.some(filterFunc)) {
            // 全局组件如果已经在界面上了，就无需再次载入了
            return;
          }
        }
        this.propsData[id] = interactConfig;
        const component = compsMap[componentId];
        const { url, workerInfo } = component;
        let mainUrl = url;
        if (isMainThread()) {
          // worker环境需要加载对应的主线程的组件js
          mainUrl = workerInfo.main;
          // 初始化worker组件
          await this.initWorker({
            componentId,
            id,
            componentUrl: workerInfo.worker,
          }, {
            root: 'true', // 根结点需要标记为true
            ...interactConfig,
          }, workerInfo.childrenCfg);
        }

        if (Vue.options.components[`V${componentId}`]) {
          log('init component1', componentId);
        } else {
          await this.loadComps(mainUrl);
          log('init component2', componentId);
        }
        newCompsList.push({
          componentId,
          id,
          isGlobal: interactItem.type === 2,
        });
      }));

      this.compsList = this.compsList.concat(newCompsList);

      // 上报SLA指标：互动衔接流畅度耗时
      // 这里是video产生切换后，到组件ready的时间差，单位是毫秒
      if (interactInfo.nodeChangeEndTime) {
        this.$nextTick(() => {
          this.iBridge.invoke(ChannelNameSpace.engine, 'reportSla', {
            value: Number(new Date()) - interactInfo.nodeChangeEndTime,
            id: Indicators.TIME,
          });
        });
      }

      if (isMainThread()) { // 释放
        setTimeout(() => {
          this.releaseWorkerInitEventAndData();
        });
      }
    });

    // 监听v数值变化
    this.iBridge.on(ChannelNameSpace.engine, 'vUpdate', async (data) => {
      this.iBridge.utils.updateValues(data);
    });

    // 监听组件数据变化
    this.iBridge.on(ChannelNameSpace.engine, 'propUpdate', async ({ id, data }) => {
      this.propsData[id] = Object.assign(this.propsData[id] || {}, data);
      this.$forceUpdate();
    });

    // 监听组件新增
    this.iBridge.on(ChannelNameSpace.engine, 'compAdded', async ({ id, componentId, data }) => {
      this.propsData[id] = Object.assign(this.propsData[id] || {}, data);
      if (!this.compsList.some(compInfo => compInfo.id === id)) {
        this.compsList.push({
          id,
          componentId,
        });
      }
    });

    // 监听组件新增(包含url)
    this.iBridge.on(ChannelNameSpace.engine, 'interactAdded', async ({ interact, template }) => {
      const { componentId, interactConfig: data, id } = interact;
      if (!Vue.options.components[`V${componentId}`]) {
        await this.loadComps(template.url || '');
      }
      this.propsData[id] = Object.assign(this.propsData[id] || {}, data);
      if (!this.compsList.some(compInfo => compInfo.id === id)) {
        this.compsList.push({
          id,
          componentId,
        });
      }
    });

    // 监听组件删除
    this.iBridge.on(ChannelNameSpace.engine, 'compRemoved', async ({ id }) => {
      this.propsData[id] = null;
      this.compsList = this.compsList.filter(compInfo => compInfo.id !== id);
    });

    this.iBridge.invoke(ChannelNameSpace.engine, 'init');
  },
};
